import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  getChecklistTasks,
  createChecklistTask,
  updateChecklistTask,
  deleteChecklistTask,
  getChecklistCompilazioni,
  createChecklistCompilazione,
  updateChecklistCompilazione,
  confermaChecklistCompilazione,
  deleteChecklistCompilazione,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Check, X, AlertTriangle, Clock, 
  ClipboardCheck, ClipboardList, Eye, Pencil, 
  Settings, CheckCircle2, XCircle, AlertCircle,
  User, ChevronUp, ChevronDown
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const Checklist = () => {
  const [tasks, setTasks] = useState([]);
  const [compilazioni, setCompilazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form visibility
  const [showCompilaForm, setShowCompilaForm] = useState(false);
  const [showConfermaForm, setShowConfermaForm] = useState(false);
  const [showTaskManager, setShowTaskManager] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  
  const [selectedCompilazione, setSelectedCompilazione] = useState(null);
  const [editingCompilazione, setEditingCompilazione] = useState(false); // true se stiamo modificando una checklist esistente
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Form states
  const [newTask, setNewTask] = useState({ nome: '', descrizione: '' });
  const [editingTask, setEditingTask] = useState(null);
  const [risposte, setRisposte] = useState([]);
  const [noteGenerali, setNoteGenerali] = useState('');
  const [segnalazioni, setSegnalazioni] = useState([]);
  const [noteConferma, setNoteConferma] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tasksData, compilazioniData] = await Promise.all([
        getChecklistTasks(),
        getChecklistCompilazioni()
      ]);
      setTasks(tasksData.filter(t => t.attivo));
      setCompilazioni(compilazioniData);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  // === TASK MANAGEMENT (Admin) ===
  const handleCreateTask = async () => {
    if (!newTask.nome.trim()) {
      toast.error('Inserisci il nome del compito');
      return;
    }
    try {
      await createChecklistTask({
        nome: newTask.nome,
        descrizione: newTask.descrizione,
        ordine: tasks.length
      });
      toast.success('Compito aggiunto!');
      setNewTask({ nome: '', descrizione: '' });
      loadData();
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;
    try {
      await updateChecklistTask(editingTask.id, {
        nome: editingTask.nome,
        descrizione: editingTask.descrizione
      });
      toast.success('Compito aggiornato!');
      setEditingTask(null);
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo compito?')) return;
    try {
      await deleteChecklistTask(taskId);
      toast.success('Compito eliminato!');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // === COMPILAZIONE CHECKLIST ===
  const resetCompilaForm = () => {
    setRisposte(tasks.map(t => ({
      task_id: t.id,
      task_nome: t.nome,
      stato: 'fatto',
      motivo: ''
    })));
    setNoteGenerali('');
    setSelectedCompilazione(null);
    setEditingCompilazione(false);
  };

  const toggleCompilaForm = () => {
    if (!showCompilaForm) {
      if (tasks.length === 0) {
        toast.error('Nessun compito configurato. Chiedi all\'admin di aggiungere i compiti.');
        return;
      }
      resetCompilaForm();
      setShowConfermaForm(false);
    }
    setShowCompilaForm(!showCompilaForm);
  };

  const handleRispostaChange = (taskId, stato) => {
    setRisposte(prev => prev.map(r => 
      r.task_id === taskId ? { ...r, stato, motivo: stato === 'non_fatto_motivo' ? r.motivo : '' } : r
    ));
  };

  const handleMotivoChange = (taskId, motivo) => {
    setRisposte(prev => prev.map(r => 
      r.task_id === taskId ? { ...r, motivo } : r
    ));
  };

  const handleSubmitCompilazione = async (e) => {
    e.preventDefault();
    
    const invalid = risposte.find(r => r.stato === 'non_fatto_motivo' && !r.motivo.trim());
    if (invalid) {
      toast.error('Inserisci il motivo per tutti i compiti "non fatto perché"');
      return;
    }

    setSaving(true);
    try {
      if (editingCompilazione && selectedCompilazione) {
        // Modifica checklist esistente
        await updateChecklistCompilazione(selectedCompilazione.id, {
          risposte,
          note_generali: noteGenerali || null
        });
        toast.success('Checklist aggiornata!');
      } else {
        // Nuova checklist
        await createChecklistCompilazione({
          risposte,
          note_generali: noteGenerali || null
        });
        toast.success('Checklist compilata! In attesa di conferma dal collega entrante.');
      }
      setShowCompilaForm(false);
      resetCompilaForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella compilazione');
    } finally {
      setSaving(false);
    }
  };

  // === CONFERMA CHECKLIST ===
  const startConferma = (compilazione) => {
    setSelectedCompilazione(compilazione);
    setSegnalazioni([]);
    setNoteConferma('');
    setShowCompilaForm(false);
    setShowConfermaForm(true);
  };

  const addSegnalazione = (taskId, taskNome) => {
    if (segnalazioni.find(s => s.task_id === taskId)) {
      toast.error('Hai già segnalato questo compito');
      return;
    }
    setSegnalazioni(prev => [...prev, { task_id: taskId, task_nome: taskNome, nota: '' }]);
  };

  const removeSegnalazione = (taskId) => {
    setSegnalazioni(prev => prev.filter(s => s.task_id !== taskId));
  };

  const handleSegnalazioneNotaChange = (taskId, nota) => {
    setSegnalazioni(prev => prev.map(s => 
      s.task_id === taskId ? { ...s, nota } : s
    ));
  };

  const handleSubmitConferma = async (e) => {
    e.preventDefault();
    
    const invalid = segnalazioni.find(s => !s.nota.trim());
    if (invalid) {
      toast.error('Inserisci una nota per tutte le segnalazioni');
      return;
    }

    setSaving(true);
    try {
      await confermaChecklistCompilazione(selectedCompilazione.id, {
        segnalazioni: segnalazioni.length > 0 ? segnalazioni : null,
        note_conferma: noteConferma || null
      });
      toast.success(segnalazioni.length > 0 
        ? 'Checklist confermata con segnalazioni!' 
        : 'Checklist confermata!');
      setShowConfermaForm(false);
      setSelectedCompilazione(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella conferma');
    } finally {
      setSaving(false);
    }
  };

  // === VIEW DETAILS ===
  const viewDetails = (compilazione) => {
    setSelectedCompilazione(compilazione);
    setShowViewDialog(true);
  };

  // === EDIT CHECKLIST ===
  const canEdit = (c) => {
    // Può modificare solo se non è già confermata
    if (c.stato !== 'in_attesa_conferma') return false;
    // Admin può sempre modificare
    if (isAdmin) return true;
    // Chi l'ha creata può modificarla
    return c.dipendente_uscente_id === user?.id;
  };

  const startEdit = (compilazione) => {
    setSelectedCompilazione(compilazione);
    setRisposte(compilazione.risposte.map(r => ({
      task_id: r.task_id,
      task_nome: r.task_nome,
      stato: r.stato,
      motivo: r.motivo || ''
    })));
    setNoteGenerali(compilazione.note_generali || '');
    setShowConfermaForm(false);
    setShowCompilaForm(true);
    setEditingCompilazione(true);
  };

  // === HELPERS ===
  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'in_attesa_conferma':
        return <Badge className="bg-yellow-500"><Clock className="h-3 w-3 mr-1" /> In attesa</Badge>;
      case 'confermata':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" /> Confermata</Badge>;
      case 'con_problemi':
        return <Badge className="bg-red-500"><AlertCircle className="h-3 w-3 mr-1" /> Problemi</Badge>;
      default:
        return <Badge>{stato}</Badge>;
    }
  };

  const getRispostaIcon = (stato) => {
    switch (stato) {
      case 'fatto':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'non_fatto':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'non_fatto_motivo':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      default:
        return null;
    }
  };

  const pendenti = compilazioni.filter(c => c.stato === 'in_attesa_conferma');
  const canConferma = (c) => c.stato === 'in_attesa_conferma' && c.dipendente_uscente_id !== user?.id;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="checklist-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Checklist Cambio Turno</h1>
          <p className="text-muted-foreground mt-1">Gestisci i compiti del cambio turno</p>
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => setShowTaskManager(true)} data-testid="manage-tasks-btn">
            <Settings className="h-4 w-4 mr-2" />
            Gestisci Compiti
          </Button>
        )}
      </div>

      {/* Alert checklist pendenti */}
      {pendenti.length > 0 && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-yellow-500" />
              <div className="flex-1">
                <p className="font-semibold text-yellow-500">
                  {pendenti.length} checklist in attesa di conferma
                </p>
                <p className="text-sm text-muted-foreground">
                  Clicca su "Conferma" per verificare e confermare
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Compila Checklist - Card espandibile */}
      <Card className={editingCompilazione ? "border-yellow-500/30" : "border-primary/30"}>
        <CardHeader 
          className="cursor-pointer flex flex-row items-center justify-between py-4"
          onClick={toggleCompilaForm}
        >
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardCheck className={`h-5 w-5 ${editingCompilazione ? 'text-yellow-500' : 'text-primary'}`} />
            {editingCompilazione ? 'Modifica Checklist' : 'Compila Checklist Fine Turno'}
          </CardTitle>
          <Button variant="ghost" size="icon">
            {showCompilaForm ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </Button>
        </CardHeader>
        
        {showCompilaForm && (
          <CardContent className="pt-0">
            <form onSubmit={handleSubmitCompilazione} className="space-y-4">
              {/* Lista compiti */}
              <div className="space-y-3">
                {risposte.map((risposta, idx) => (
                  <div key={risposta.task_id} className="p-4 bg-secondary/30 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="font-medium flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-sm">
                          {idx + 1}
                        </span>
                        {risposta.task_nome}
                      </p>
                      {getRispostaIcon(risposta.stato)}
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={risposta.stato === 'fatto' ? 'default' : 'outline'}
                        className={risposta.stato === 'fatto' ? 'bg-green-600 hover:bg-green-700' : ''}
                        onClick={() => handleRispostaChange(risposta.task_id, 'fatto')}
                      >
                        <Check className="h-4 w-4 mr-1" /> Fatto
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={risposta.stato === 'non_fatto' ? 'default' : 'outline'}
                        className={risposta.stato === 'non_fatto' ? 'bg-red-600 hover:bg-red-700' : ''}
                        onClick={() => handleRispostaChange(risposta.task_id, 'non_fatto')}
                      >
                        <X className="h-4 w-4 mr-1" /> Non fatto
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={risposta.stato === 'non_fatto_motivo' ? 'default' : 'outline'}
                        className={risposta.stato === 'non_fatto_motivo' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                        onClick={() => handleRispostaChange(risposta.task_id, 'non_fatto_motivo')}
                      >
                        <AlertTriangle className="h-4 w-4 mr-1" /> Non fatto perché...
                      </Button>
                    </div>

                    {risposta.stato === 'non_fatto_motivo' && (
                      <Input
                        placeholder="Motivo (es. manca carta igienica)"
                        value={risposta.motivo}
                        onChange={(e) => handleMotivoChange(risposta.task_id, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Note generali */}
              <div className="space-y-2">
                <Label>Note generali (opzionale)</Label>
                <Textarea
                  placeholder="Eventuali note per il collega entrante..."
                  value={noteGenerali}
                  onChange={(e) => setNoteGenerali(e.target.value)}
                  rows={3}
                  data-testid="checklist-note"
                />
              </div>

              {/* Pulsante invio */}
              <Button 
                type="submit" 
                className={`w-full h-12 ${editingCompilazione ? 'bg-yellow-600 hover:bg-yellow-700' : ''}`} 
                disabled={saving} 
                data-testid="submit-checklist-btn"
              >
                {saving ? 'Salvataggio...' : editingCompilazione ? 'Salva Modifiche' : 'Invia Checklist'}
              </Button>
            </form>
          </CardContent>
        )}
      </Card>

      {/* Form Conferma Checklist - Card espandibile */}
      {selectedCompilazione && showConfermaForm && (
        <Card className="border-green-500/30">
          <CardHeader 
            className="cursor-pointer flex flex-row items-center justify-between py-4"
            onClick={() => setShowConfermaForm(!showConfermaForm)}
          >
            <CardTitle className="text-lg flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              Conferma Checklist di {selectedCompilazione.dipendente_uscente_nome}
            </CardTitle>
            <Button variant="ghost" size="icon">
              {showConfermaForm ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </Button>
          </CardHeader>
          
          <CardContent className="pt-0">
            <form onSubmit={handleSubmitConferma} className="space-y-4">
              {/* Info compilazione */}
              <div className="p-3 bg-secondary/30 rounded-lg text-sm">
                <p className="text-muted-foreground">
                  Compilata il {format(parseISO(selectedCompilazione.data_compilazione), 'dd/MM/yyyy HH:mm', { locale: it })}
                </p>
              </div>

              {/* Riepilogo risposte */}
              <div className="space-y-2">
                {selectedCompilazione.risposte.map((r) => (
                  <div 
                    key={r.task_id} 
                    className={`p-3 rounded-lg flex items-center justify-between ${
                      r.stato !== 'fatto' ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-secondary/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {getRispostaIcon(r.stato)}
                      <div>
                        <p className="font-medium">{r.task_nome}</p>
                        {r.motivo && (
                          <p className="text-sm text-orange-500">Motivo: {r.motivo}</p>
                        )}
                      </div>
                    </div>
                    {/* Bottone Segnala per TUTTI i compiti (anche quelli fatti male) */}
                    {!segnalazioni.find(s => s.task_id === r.task_id) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                        onClick={() => addSegnalazione(r.task_id, r.task_nome)}
                      >
                        <AlertTriangle className="h-4 w-4 mr-1" /> Segnala
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Segnalazioni */}
              {segnalazioni.length > 0 && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-3">
                  <p className="font-medium text-red-500 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Le tue segnalazioni
                  </p>
                  {segnalazioni.map((s) => (
                    <div key={s.task_id} className="flex items-start gap-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{s.task_nome}</p>
                        <Input
                          placeholder="Descrivi il problema trovato..."
                          value={s.nota}
                          onChange={(e) => handleSegnalazioneNotaChange(s.task_id, e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => removeSegnalazione(s.task_id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Note dal collega */}
              {selectedCompilazione.note_generali && (
                <div className="p-3 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Note dal collega:</p>
                  <p>{selectedCompilazione.note_generali}</p>
                </div>
              )}

              {/* Note conferma */}
              <div className="space-y-2">
                <Label>Note conferma (opzionale)</Label>
                <Textarea
                  placeholder="Eventuali note..."
                  value={noteConferma}
                  onChange={(e) => setNoteConferma(e.target.value)}
                  rows={2}
                />
              </div>

              {/* Pulsanti */}
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowConfermaForm(false);
                    setSelectedCompilazione(null);
                  }}
                >
                  Annulla
                </Button>
                <Button 
                  type="submit"
                  className={`flex-1 ${segnalazioni.length > 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                  disabled={saving}
                >
                  {saving ? 'Salvataggio...' : segnalazioni.length > 0 ? 'Conferma con Segnalazioni' : 'Conferma OK'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Lista compilazioni */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Storico Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          {compilazioni.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nessuna checklist compilata</p>
              <p className="text-sm text-muted-foreground">Espandi "Compila Checklist" per iniziare</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Uscente</TableHead>
                    <TableHead>Entrante</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-center">Problemi</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {compilazioni.map((c) => (
                    <TableRow key={c.id} data-testid={`checklist-row-${c.id}`}>
                      <TableCell className="font-mono">
                        {format(parseISO(c.data_compilazione), 'dd/MM/yy HH:mm', { locale: it })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {c.dipendente_uscente_nome}
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.dipendente_entrante_nome ? (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {c.dipendente_entrante_nome}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatoBadge(c.stato)}</TableCell>
                      <TableCell className="text-center">
                        {c.problemi_count > 0 ? (
                          <Badge variant="destructive">{c.problemi_count}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => viewDetails(c)}
                            title="Visualizza"
                            data-testid={`view-checklist-${c.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canEdit(c) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => startEdit(c)}
                              title="Modifica"
                              data-testid={`edit-checklist-${c.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canConferma(c) && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => startConferma(c)}
                              data-testid={`confirm-checklist-${c.id}`}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Conferma
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive"
                              onClick={async () => {
                                if (window.confirm('Eliminare questa checklist?')) {
                                  await deleteChecklistCompilazione(c.id);
                                  toast.success('Eliminata');
                                  loadData();
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Gestione Task (Admin) - resta modal per gestione */}
      <Dialog open={showTaskManager} onOpenChange={setShowTaskManager}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Gestione Compiti Checklist
            </DialogTitle>
            <DialogDescription>
              Aggiungi, modifica o rimuovi i compiti della checklist
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Form nuovo task */}
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      placeholder="Nome compito (es. Pulire bagno)"
                      value={newTask.nome}
                      onChange={(e) => setNewTask({ ...newTask, nome: e.target.value })}
                      data-testid="new-task-name"
                    />
                  </div>
                  <Button onClick={handleCreateTask} data-testid="add-task-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <Input
                  placeholder="Descrizione (opzionale)"
                  value={newTask.descrizione}
                  onChange={(e) => setNewTask({ ...newTask, descrizione: e.target.value })}
                  className="mt-2"
                />
              </CardContent>
            </Card>

            {/* Lista task */}
            <div className="space-y-2">
              {tasks.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  Nessun compito configurato
                </p>
              ) : (
                tasks.map((task, idx) => (
                  <Card key={task.id}>
                    <CardContent className="p-3">
                      {editingTask?.id === task.id ? (
                        <div className="space-y-2">
                          <Input
                            value={editingTask.nome}
                            onChange={(e) => setEditingTask({ ...editingTask, nome: e.target.value })}
                          />
                          <Input
                            value={editingTask.descrizione || ''}
                            onChange={(e) => setEditingTask({ ...editingTask, descrizione: e.target.value })}
                            placeholder="Descrizione"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleUpdateTask}>
                              <Check className="h-4 w-4 mr-1" /> Salva
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingTask(null)}>
                              Annulla
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{idx + 1}. {task.nome}</p>
                            {task.descrizione && (
                              <p className="text-sm text-muted-foreground">{task.descrizione}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingTask(task)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive"
                              onClick={() => handleDeleteTask(task.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Visualizza Dettagli */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Dettaglio Checklist
            </DialogTitle>
          </DialogHeader>

          {selectedCompilazione && (
            <div className="space-y-4">
              {/* Info generali */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Uscente</p>
                    <p className="font-medium">{selectedCompilazione.dipendente_uscente_nome}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(parseISO(selectedCompilazione.data_compilazione), 'dd/MM/yy HH:mm', { locale: it })}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Entrante</p>
                    {selectedCompilazione.dipendente_entrante_nome ? (
                      <>
                        <p className="font-medium">{selectedCompilazione.dipendente_entrante_nome}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(parseISO(selectedCompilazione.data_conferma), 'dd/MM/yy HH:mm', { locale: it })}
                        </p>
                      </>
                    ) : (
                      <p className="text-muted-foreground">In attesa</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="flex items-center gap-2">
                {getStatoBadge(selectedCompilazione.stato)}
                {selectedCompilazione.problemi_count > 0 && (
                  <Badge variant="destructive">{selectedCompilazione.problemi_count} problemi</Badge>
                )}
              </div>

              {/* Risposte */}
              <div className="space-y-2">
                <p className="font-medium">Compiti:</p>
                {selectedCompilazione.risposte.map((r) => (
                  <div key={r.task_id} className="flex items-center gap-2 p-2 bg-secondary/30 rounded">
                    {getRispostaIcon(r.stato)}
                    <div className="flex-1">
                      <p className="font-medium">{r.task_nome}</p>
                      {r.motivo && <p className="text-sm text-orange-500">{r.motivo}</p>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Segnalazioni entrante */}
              {selectedCompilazione.segnalazioni_entrante && selectedCompilazione.segnalazioni_entrante.length > 0 && (
                <Card className="border-red-500/50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-base text-red-500">Segnalazioni entrante</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {selectedCompilazione.segnalazioni_entrante.map((s) => (
                      <div key={s.task_id} className="p-2 bg-red-500/10 rounded">
                        <p className="font-medium">{s.task_nome}</p>
                        <p className="text-sm">{s.nota}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {selectedCompilazione.note_generali && (
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Note uscente:</p>
                    <p>{selectedCompilazione.note_generali}</p>
                  </CardContent>
                </Card>
              )}

              {selectedCompilazione.note_conferma && (
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">Note entrante:</p>
                    <p>{selectedCompilazione.note_conferma}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Checklist;
